import { Injectable } from '@angular/core';
import * as showdown from 'showdown';
import DOMPurify from 'dompurify';

/**
 * Service for safely converting markdown to HTML while preventing HTML injection attacks.
 * This service uses showdown for markdown conversion and DOMPurify for HTML sanitization.
 */
@Injectable({ providedIn: 'root' })
export class MarkdownSanitizerService {
  private converter: showdown.Converter;

  constructor() {
    this.converter = new showdown.Converter({
      noHeaderId: true,
      literalMidWordUnderscores: true,
      strikethrough: true,
      tables: true,
      tasklists: true,
      simpleLineBreaks: false,
      openLinksInNewWindow: true
    });
  }

  /**
   * Converts markdown to sanitized HTML.
   * This method first converts markdown to HTML using showdown,
   * then sanitizes the HTML using DOMPurify to remove dangerous content.
   *
   * @param markdown The markdown text to convert
   * @returns Sanitized HTML safe for rendering
   *
   * @example
   * markdownToSafeHtml('# Hello\n**Bold** text')
   * // Returns: '<h1>Hello</h1>\n<p><strong>Bold</strong> text</p>'
   *
   * markdownToSafeHtml('# Hello\n<script>alert(1)</script>')
   * // Returns: '<h1>Hello</h1>\n' (script tag removed)
   */
  markdownToSafeHtml(markdown: string): string {
    if (!markdown) {
      return '';
    }

    // Convert markdown to HTML
    const html = this.converter.makeHtml(markdown);

    // Sanitize with DOMPurify to remove dangerous HTML
    // Allow only safe markdown-generated tags
    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS: [
        // Text formatting
        'p', 'br', 'span', 'div',
        // Headers
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        // Text styles
        'strong', 'em', 'b', 'i', 'u', 's', 'del', 'mark',
        // Block elements
        'blockquote', 'pre', 'code',
        // Lists
        'ul', 'ol', 'li',
        // Links
        'a',
        // Tables
        'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td',
        // Horizontal rule
        'hr',
        // Task lists
        'input'
      ],
      ALLOWED_ATTR: [
        // Link attributes
        'href', 'title', 'target', 'rel',
        // Table attributes
        'align', 'colspan', 'rowspan',
        // Task list attributes
        'type', 'checked', 'disabled'
      ],
      // Allow only safe URL schemes
      ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i,
      // Keep whitespace
      KEEP_CONTENT: true,
      // Return as string
      RETURN_DOM: false,
      RETURN_DOM_FRAGMENT: false
    });
  }

  /**
   * Converts HTML back to markdown.
   * This is a convenience method that wraps showdown's makeMarkdown.
   *
   * @param html The HTML to convert to markdown
   * @returns Markdown text
   */
  htmlToMarkdown(html: string): string {
    if (!html) {
      return '';
    }
    return this.converter.makeMarkdown(html);
  }
}
